// MoveToolPropertiesPopup.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Button, CloseButton, Dialog, HStack, Input, Portal, Text, VStack } from "@chakra-ui/react";
import { createUniqueId, matchKeyInText, showToast } from "../../../utilies/Utility";
import { JulianDate, PropertyBag } from "cesium";
import Type from "../../../utilies/Types";

function isExactIdKey(key) {
  return typeof key === "string" && key.toLowerCase() === "id";
}

function getMaxIdFromDataSource(ds) {
  if (!ds?.entities?.values) return null;
  const now = JulianDate.now();
  let max = -Infinity;

  for (const e of ds.entities.values) {
    const v = e?.properties?.getValue?.(now) || {};
    const n = Number(v?.id);
    if (Number.isFinite(n) && n > max) max = n;
  }

  return max === -Infinity ? null : max;
}

function getNextIdFromDataSource(ds) {
  const max = getMaxIdFromDataSource(ds);
  const base = Number.isFinite(max) ? max : 0;
  return base + 2;
}

function getTemplatePropsFromDataSource(ds) {
  const now = JulianDate.now();
  const sample = ds?.entities?.values?.[0] || null;
  if (!sample?.properties) return {};
  const v = sample.properties.getValue?.(now);
  if (v && typeof v === "object") return v;
  // PropertyBag fallback
  if (sample.properties instanceof PropertyBag) {
    const out = {};
    for (const k in sample.properties._properties) {
      try {
        out[k] = sample.properties[k]?.getValue?.(now);
      } catch {}
    }
    return out;
  }
  return {};
}

const LABELS = {
  virtualline: "V = Move to Virtual",
  curbstone: "C = Move to Curbstone",
  roadedge: "R = Move to Road Edge",
  lanemarker: "L = Move to Lane Marker",
};

const MoveToolPropertiesPopup = ({ open, onClose, targetKey, mapObjects, onConfirm }) => {
  const targetLayer = useMemo(() => {
    if (!targetKey) return null;
    const list = Array.isArray(mapObjects) ? mapObjects : [];
    for (const o of list) {
      if (!o?.file_name || !o?.dataSource) continue;
      if (matchKeyInText(targetKey, o.file_name)) return o;
    }
    return null;
  }, [mapObjects, targetKey]);

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!targetLayer?.dataSource) {
      setRows([]);
      return;
    }

    const templateProps = getTemplatePropsFromDataSource(targetLayer.dataSource) || {};
    const keys = Object.keys(templateProps).filter((k) => !isExactIdKey(k));

    const nextRows = keys.map((k) => ({
      index: createUniqueId(),
      key: k,
      value: templateProps[k] ?? "",
    }));

    setRows(nextRows);
  }, [open, targetLayer]);

  const addBlankRow = () => {
    setRows((prev) => [
      ...prev,
      {
        index: createUniqueId(),
        key: "",
        value: "",
      },
    ]);
  };

const save = async () => {
  if (!targetLayer?.dataSource) return;

  setBusy(true);
  try {
    const propsObj = {};
    for (const r of rows) {
      const k = String(r?.key || "").trim();
      if (!k) continue;
      if (isExactIdKey(k)) continue;
      propsObj[k] = r?.value;
    }

    propsObj.id = getNextIdFromDataSource(targetLayer.dataSource);

    await onConfirm?.(propsObj);

    // Success message
    showToast("Moved successfully.", Type.NOTIFICATION.SUCCESS, {
      duration: 2000,
      closable: true,
    });
  } catch (e) {
    // Optional: failure message
    showToast("Move failed.", Type.NOTIFICATION.ERROR, {
      duration: 2500,
      closable: true,
    });
    throw e;
  } finally {
    setBusy(false);
  }
};


  return (
    <Dialog.Root placement="top" motionPreset="slide-in-top" open={open} onOpenChange={(e) => !e.open && onClose?.()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Move Properties</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <VStack alignItems="start" gap={3}>
                <Text fontSize="sm" color="gray.600">
                  {LABELS[targetKey] || "Select destination"}
                </Text>

                {!targetLayer && (
                  <Text fontSize="sm" color="red.500">
                    Target layer not found in mapObjects.
                  </Text>
                )}

                {targetLayer &&
                  rows.map((item) => (
                    <HStack gap={2} key={item.index} width="full">
                      <Input
                        placeholder="Key"
                        value={item.key}
                        onChange={(e) => {
                          const nextKey = e.target.value;
                          setRows((prev) => prev.map((r) => (r.index === item.index ? { ...r, key: nextKey } : r)));
                        }}
                      />
                      <Input
                        placeholder="Value"
                        value={item.value}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setRows((prev) => prev.map((r) => (r.index === item.index ? { ...r, value: nextValue } : r)));
                        }}
                      />
                      <Button colorPalette="red" onClick={() => setRows((prev) => prev.filter((r) => r.index !== item.index))} disabled={busy}>
                        -
                      </Button>
                    </HStack>
                  ))}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button onClick={addBlankRow} disabled={!targetLayer || busy} colorPalette="black">
                +
              </Button>

              <Dialog.ActionTrigger asChild>
                <Button variant="outline" onClick={() => onClose?.()} disabled={busy}>
                  Cancel
                </Button>
              </Dialog.ActionTrigger>

              <Button onClick={save} disabled={!targetLayer || busy}>
                Save
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default MoveToolPropertiesPopup;
